import React, { useEffect, useState, useCallback } from 'react';
import './ManageTradeEntryTracker.css';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";

const MULTI_TIMEFRAME_CONFLUENCE = 'MULTI_TIMEFRAME_CONFLUENCE';

const TREND_SHIFT_SIGNAL_OPTIONS = [
  'FIRST_EMA200_BAND_CROSS_AFTER_TREND_SHIFT',
  'FIRST_EMA50_BAND_CROSS_AFTER_TREND_SHIFT',
  'FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AFTER_TREND_SHIFT',
  'FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AT_EMA200_BAND_AFTER_TREND_SHIFT',
  'FIRST_TREND_ALIGNED_STOCH_RSI_RESET_AT_EMA50_BAND_AFTER_TREND_SHIFT'
];

function ManageTradeEntryTracker() {
  const { workspace } = useWorkspace();
  const BASE_SERVICE_URL =
    '/api/bot/ui/get';

  const withWorkspace = useCallback((row = {}) => ({
    ...row,
    exchange: row.exchange || workspace.exchange,
    marketType: row.marketType || workspace.marketType,
  }), [workspace]);



  const [dataList, setDataList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [coinDropdownOptions, setCoinDropdownOptions] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedConfluenceIds, setExpandedConfluenceIds] = useState(new Set());
  const [confluenceProgressByTracker, setConfluenceProgressByTracker] = useState({});
  const [filters, setFilters] = useState({
    id: '',
    exchange: '',
    marketType: '',
    symbol: '',
    timeFrame: '',
    tradeSide: '',
    tradeEntryType: '',
    tradeEntryTrackerType: '',
    tradeEntryTrackerValue: '',
    volumeBreakoutRequired: '',
    expiryEnabled: '',
    expiresAt: '',
    margin: '',
    leverage: '',
    tradeType: '',
    orderType: '',
    tradeEntryTrackerStartedOn: '',
    tradeEntryTrackerStoppedOn: '',
    expectedCandleColorToOpen: '',
    attemptAllowed: '',
    attemptCompleted: '',
    activeFlag: 'Y',
    signalRequestId: '',
    positionOpened: '',
    avgOpenPrice: '',
    stopLossHit: '',
    takeProfitHit: '',
    trailingStopLossRequired: '',
    trailingStopLossType: '',
    breakEvenRequired: '',
    partialTakeProfitRequired: '',
    dcaRequired: '',
    tslMinProfitPercent: '',
    tslCallbackPercent: '',
    beMinProfitPercent: '',
    ptpMinProfitPercent: '',
    dcaMinProfitPercent: '',
    trailingStopLossSRTimeFrame: '',
    breakEvenSRTimeFrame: '',
    partialTakeProfitSRTimeFrame: '',
    dcaTimeFrame: '',
    ptpCloseSizePercent: '',
    dcaAddSizePercent: '',

    comments: '',
    journaling: '',
    trackerSpecialIdentifier: '',
    createdBy: '',
    createdDate: '',
    modifiedBy: '',
    modifiedDate: '',
    indiaTimestamp: '',
    sgTimestamp: '',
    });


  const timeFrameOptions = ['5', '15', '30', '60', '240', '480', '720', '1440'];
  const tradeTypeOptions = ['DCA', 'FRESH'];
  const orderTypeOptions = ['market', 'limit'];
  const attemptAllowedOptions = ['1', '2','3','4','5'];
  const attemptCompletedOptions = ['0','1', '2','3','4','5'];

  const activeFlagOptions = ['Y', 'N'];
  const tradeSideOptions = ['open_long', 'open_short'];
  const tradeEntryTypeOptions = ['immediate', 'upcoming trade setup'];
  const leverageOptions = ['1', '2','3','5','10','15','20'];


  const [newRow, setNewRow] = useState({
    id: '',
    exchange: workspace.exchange,
    marketType: workspace.marketType,
    symbol: '',
    timeFrame: '',
    tradeSide: '',
    tradeEntryType: '',
    tradeEntryTrackerType: '',    
    tradeEntryTrackerValue: '',
    volumeBreakoutRequired: false,
    expiryEnabled: false,
    expiresAt: '',
    margin: '',
    leverage: '',
    tradeType: '',
    orderType: '',
    tradeEntryTrackerStartedOn: '',
    tradeEntryTrackerStoppedOn: '',
    expectedCandleColorToOpen: '',
    attemptAllowed: '',
    attemptCompleted: '',
    activeFlag: 'Y',
    signalRequestId: '',
    positionOpened: false,
    avgOpenPrice: '',
    stopLossHit: false,
    takeProfitHit: false,    
    trailingStopLossRequired: false,
    trailingStopLossType: '',
    breakEvenRequired: false,
    partialTakeProfitRequired: false,
    dcaRequired: false,
    tslMinProfitPercent: '',
    tslCallbackPercent: '',
    beMinProfitPercent: '',
    ptpMinProfitPercent: '',
    dcaMinProfitPercent: '',
    trailingStopLossSRTimeFrame: '',
    breakEvenSRTimeFrame: '',
    partialTakeProfitSRTimeFrame: '',
    dcaTimeFrame: '',
    ptpCloseSizePercent: '',
    dcaAddSizePercent: '',
    comments: '',
    journaling: '',
    trackerSpecialIdentifier: '',
    createdBy: '',
    createdDate: '',
    modifiedBy: '',
    modifiedDate: '',
    indiaTimestamp: '',
    sgTimestamp: '',
  });

  const [editIndex, setEditIndex] = useState(null);
  const [editRowData, setEditRowData] = useState(null);

  const sortRows = useCallback((rows, config = sortConfig) => {
    const { key, direction } = config;
    return [...rows].sort((a, b) => {
      let aVal = a?.[key] ?? '';
      let bVal = b?.[key] ?? '';

      if (
        ['timeFrame', 'attemptAllowed', 'attemptCompleted', 'id',
          'trailingStopLossSRTimeFrame', 'breakEvenSRTimeFrame',
          'partialTakeProfitSRTimeFrame', 'dcaTimeFrame'].includes(key)
      ) {
        aVal = Number(aVal || 0);
        bVal = Number(bVal || 0);
      }

      if (
        ['createdDate', 'modifiedDate', 'indiaTimestamp', 'sgTimestamp'].includes(key)
      ) {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortConfig]);

  // Ã¢Å“â€¦ Fetch records
  const fetchRecords = useCallback(async () => {
    try {
      const res = await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/getAllTradeEntryTrackerRecords`, workspace), {
        headers: getApiHeaders()
      });
      const data = await res.json();
      const normalizedData = ensureArray(data)
        .filter(Boolean)
        .map((item) => withWorkspace(item));
      setDataList(normalizedData);
      setFilteredList(sortRows(normalizedData.filter((item) => item.activeFlag?.toString() === 'Y')));
    } catch (error) {
      console.error('Trade entry tracker fetch failed:', error);
      setDataList([]);
      setFilteredList([]);
    }
  }, [BASE_SERVICE_URL, sortRows, withWorkspace, workspace]);

  // Ã¢Å“â€¦ Helper: Get unique non-empty values for a column from dataList
    const getUniqueValues = useCallback(
    (key) => {
        const values = Array.from(
        new Set(
            dataList
            .filter(Boolean)
            .map((item) => (item[key] !== null && item[key] !== undefined ? String(item[key]) : ''))
            .filter((v) => v.trim() !== '')
        )
        );
        return values.sort();
    },
    [dataList]
    );

  const getTradeEntryTrackerTypeOptions = useCallback(() => (
    Array.from(new Set([...TREND_SHIFT_SIGNAL_OPTIONS, ...getUniqueValues('tradeEntryTrackerType')])).sort()
  ), [getUniqueValues]);



  const fetchCoinDropdown = useCallback(async () => {
    try {
      const res = await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/getRecentActiveAlertSymbols`, workspace), {
        headers: getApiHeaders()
      });
      const coins = await res.json();
      setCoinDropdownOptions(ensureArray(coins));
    } catch (err) {
      console.error('Coin dropdown fetch failed:', err);
    }
  }, [BASE_SERVICE_URL, workspace]);

  useEffect(() => {
    fetchRecords();
    fetchCoinDropdown();
  }, [fetchCoinDropdown, fetchRecords]);

    const applyFilters = useCallback(() => {
    const filtered = dataList.filter((item) =>
        Object.entries(filters).every(([key, value]) => {
        if (value === '' || value == null) return true;
        const itemVal = item[key]?.toString() ?? '';
        return itemVal === value;
        })
    );
    setFilteredList(sortRows(filtered));
    }, [dataList, filters, sortRows]);




  useEffect(() => applyFilters(), [applyFilters]);

  // Ã¢Å“â€¦ Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    const nextSort = { key, direction };
    setSortConfig(nextSort);
    setFilteredList(sortRows(filteredList, nextSort));
  };

  const getSortIcon = (key) =>
    sortConfig.key === key ? (sortConfig.direction === 'asc' ? ' ^' : ' v') : '';

  // Ã¢Å“â€¦ CRUD Operations
  const handleAdd = async () => {
    await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/saveTradeEntryTracker`, workspace), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
      body: JSON.stringify(withWorkspace(newRow)),
    });
    setNewRow({
      id: '',
      exchange: workspace.exchange,
      marketType: workspace.marketType,
      symbol: '',
      timeFrame: '',
      tradeSide: '',
      tradeEntryType: '',
      tradeEntryTrackerType: '',
      tradeEntryTrackerValue: '',
      volumeBreakoutRequired: false,
      expiryEnabled: false,
      expiresAt: '',
      margin: '',
      leverage: '',
      tradeType: '',
      orderType: '',
      tradeEntryTrackerStartedOn: '',
      tradeEntryTrackerStoppedOn: '',
      expectedCandleColorToOpen: '',
      attemptAllowed: '',
      attemptCompleted: '',
      signalRequestId: '',
      positionOpened: false,
      avgOpenPrice: '',
      stopLossHit: false,
      takeProfitHit: false,
      trailingStopLossRequired: false,
      trailingStopLossType: '',
      breakEvenRequired: false,
      partialTakeProfitRequired: false,
      dcaRequired: false,
      tslMinProfitPercent: '',
      tslCallbackPercent: '',
      beMinProfitPercent: '',
      ptpMinProfitPercent: '',
      dcaMinProfitPercent: '',
      trailingStopLossSRTimeFrame: '',
      breakEvenSRTimeFrame: '',
      partialTakeProfitSRTimeFrame: '',
      dcaTimeFrame: '',
      ptpCloseSizePercent: '',
      dcaAddSizePercent: '',      
        
      activeFlag: 'Y',
      comments: '',
      journaling: '',
      trackerSpecialIdentifier: '',
      createdBy: '',
      createdDate: '',
      modifiedBy: '',
      modifiedDate: '',
      indiaTimestamp: '',
      sgTimestamp: '',
    });
    await fetchRecords();
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }), 300);
  };

  const handleSave = async () => {
    await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/saveTradeEntryTracker`, workspace), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
      body: JSON.stringify(withWorkspace(editRowData)),
    });
    setEditIndex(null);
    await fetchRecords();
  };

  const handleDelete = async (id) => {
    await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/deleteTradeEntryTrackerById`, workspace, { id }), { method: 'DELETE',
  headers: getApiHeaders() });
    await fetchRecords();
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected tracker(s)?`)) return;
    await fetch(buildScopedApiUrl(`${BASE_SERVICE_URL}/deleteAllTradeEntryTrackerByList`, workspace), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getApiHeaders() },
      body: JSON.stringify(ids),
    });
    setSelectedIds(new Set());
    await fetchRecords();
  };

  // Ã¢Å“â€¦ Table UI

  const toggleConfluenceProgress = async (trackerId) => {
    const nextExpanded = new Set(expandedConfluenceIds);
    if (nextExpanded.has(trackerId)) {
      nextExpanded.delete(trackerId);
      setExpandedConfluenceIds(nextExpanded);
      return;
    }

    nextExpanded.add(trackerId);
    setExpandedConfluenceIds(nextExpanded);
    if (confluenceProgressByTracker[trackerId]) {
      return;
    }

    try {
      const res = await fetch(
        buildScopedApiUrl(BASE_SERVICE_URL + '/confluenceProgress', workspace, { tradeEntryTrackerId: trackerId }),
        { headers: getApiHeaders() }
      );
      const rows = await res.json();
      setConfluenceProgressByTracker((prev) => ({
        ...prev,
        [trackerId]: ensureArray(rows),
      }));
    } catch (error) {
      console.error('Confluence progress fetch failed:', error);
      setConfluenceProgressByTracker((prev) => ({
        ...prev,
        [trackerId]: [],
      }));
    }
  };

  const tableColumns = Object.keys(newRow);

  return (
  <div className="trade-entry-page">
    <h2 className="page-title">Manage Trade Entry Tracker</h2>

    <div className="filter-actions">
      <button
        className="btn professional-clear"
        onClick={() =>
            setFilters(Object.keys(filters).reduce((acc, key) => ({ ...acc, [key]: '' }), {}))
        }
        >
        <span className="professional-clear__icon" aria-hidden="true" />
        Clear Filters
      </button>

      <button
        className="btn danger"
        disabled={selectedIds.size === 0}
        onClick={handleDeleteSelected}
      >
        Delete Selected
      </button>
    </div>

    <div className="table-wrapper">
      <table className="trade-entry-table">
        <thead>
          <tr>
            <th>S.No</th>
            <th></th>
            {tableColumns.map((col) => (
              <th key={col} onClick={() => handleSort(col)}>
                {col} {getSortIcon(col)}
              </th>
            ))}
            <th>Actions</th>
          </tr>
          
            {/* Ã¢Å“â€¦ Corrected Filter Row */}
        {/* Ã¢Å“â€¦ Full Filter Row with all dropdowns matching newRow */}
        
        {/* Ã¢Å“â€¦ Fully dynamic filter row */}
        <tr className="filter-row">
        <th></th>
        <th></th>
        {tableColumns.map((col, i) => {
            // Determine dropdown options (predefined or dynamic)
            let options = [];
            if (col === 'symbol') options = coinDropdownOptions;
            else if (col === 'timeFrame') options = timeFrameOptions;
            else if (col === 'tradeType') options = tradeTypeOptions;
            else if (col === 'orderType') options = orderTypeOptions;
            else if (col === 'tradeSide') options = tradeSideOptions;
            else if (col === 'tradeEntryType') options = tradeEntryTypeOptions;            
            else if (col === 'activeFlag') options = activeFlagOptions;
            else if (col === 'leverage') options = leverageOptions;
            else if (col === 'attemptAllowed') options = attemptAllowedOptions;
            else if (col === 'attemptCompleted')  options = attemptCompletedOptions;
            else options = getUniqueValues(col);

            return (
            <th key={i}>
                <select
                value={filters[col] ?? ''}
                onChange={(e) => setFilters({ ...filters, [col]: e.target.value })}
                >
                <option value="">All</option>
                {options.map((opt, j) => (
                    <option key={j} value={opt}>
                    {opt}
                    </option>
                ))}
                </select>
            </th>
            );
        })}
        <th></th>
        </tr>
            

        </thead>

        <tbody>
          {/* Ã¢Å“â€¦ Existing Records */}
          {filteredList.map((row, idx) => (
            <React.Fragment key={row.id || idx}>
            <tr>
              <td>{idx + 1}</td>
              <td>
                <input
                  type="checkbox"
                  checked={selectedIds.has(row.id)}
                  onChange={() => {
                    const next = new Set(selectedIds);
                    next.has(row.id) ? next.delete(row.id) : next.add(row.id);
                    setSelectedIds(next);
                  }}
                />
              </td>

              {Object.keys(newRow).map((key) =>
                editIndex === idx ? (
                  <td key={key}>
                    {typeof row[key] === 'boolean' ? (
                        <input
                        type="checkbox"
                        checked={!!editRowData[key]}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, [key]: e.target.checked })
                        }
                        />
                    ) : key === 'symbol' ? (
                        <select
                        value={editRowData.symbol}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, symbol: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {coinDropdownOptions.map((coin, i) => (
                            <option key={i} value={coin}>{coin}</option>
                        ))}
                        </select>
                    ) : key === 'orderType' ? (
                        <select
                        value={editRowData.orderType}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, orderType: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {orderTypeOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'tradeType' ? (
                        <select
                        value={editRowData.tradeType}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, tradeType: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {tradeTypeOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'timeFrame' ? (
                        <select
                        value={editRowData.timeFrame}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, timeFrame: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {timeFrameOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'attemptAllowed' ? (
                        <select
                        value={editRowData.attemptAllowed}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, attemptAllowed: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {attemptAllowedOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'attemptCompleted' ? (
                        <select
                        value={editRowData.attemptCompleted}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, attemptCompleted: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {attemptCompletedOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'activeFlag' ? (
                        <select
                        value={editRowData.activeFlag}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, activeFlag: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {activeFlagOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'tradeSide' ? (
                        <select
                        value={editRowData.tradeSide}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, tradeSide: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {tradeSideOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'tradeEntryType' ? (
                        <select
                        value={editRowData.tradeEntryType}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, tradeEntryType: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {tradeEntryTypeOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'tradeEntryTrackerType' ? (
                        <select
                        value={editRowData.tradeEntryTrackerType}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, tradeEntryTrackerType: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {getTradeEntryTrackerTypeOptions().map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'leverage' ? (
                        <select
                        value={editRowData.leverage}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, leverage: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {leverageOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : (
                        <input
                        value={editRowData[key] || ''}
                        onChange={(e) =>
                            setEditRowData({ ...editRowData, [key]: e.target.value })
                        }
                        />
                    )}
                    </td>

                ) : (
                  <td key={key}>{String(row[key] ?? '')}</td>
                )
              )}

              <td>
                {editIndex === idx ? (
                  <>
                    <button className="btn btn--save" onClick={handleSave}>Save</button>
                    <button className="btn btn--cancel" onClick={() => setEditIndex(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    {row.tradeEntryTrackerType === MULTI_TIMEFRAME_CONFLUENCE && (
                      <button className="btn btn--details" onClick={() => toggleConfluenceProgress(row.id)}>
                        {expandedConfluenceIds.has(row.id) ? 'Hide Progress' : 'Progress'}
                      </button>
                    )}
                    <button className="btn btn--edit" onClick={() => { setEditIndex(idx); setEditRowData(row); }}>Edit</button>
                    <button className="btn btn--delete" onClick={() => handleDelete(row.id)}>Delete</button>
                  </>
                )}
              </td>
            </tr>
            {expandedConfluenceIds.has(row.id) && (
              <tr className="confluence-progress-row">
                <td colSpan={tableColumns.length + 3}>
                  <div className="confluence-progress-panel">
                    <strong>Confluence Progress</strong>
                    <table>
                      <thead>
                        <tr>
                          <th>TF</th>
                          <th>Condition</th>
                          <th>Signal</th>
                          <th>Matched</th>
                          <th>Direction</th>
                          <th>Matched Close</th>
                          <th>Last Checked</th>
                          <th>Status</th>
                          <th>Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(confluenceProgressByTracker[row.id] || []).length === 0 ? (
                          <tr>
                            <td colSpan="9">No progress rows yet. The scheduler will create rows after the tracker is processed.</td>
                          </tr>
                        ) : (
                          confluenceProgressByTracker[row.id].map((progress) => (
                            <tr key={progress.id || (String(progress.ruleId || 'rule') + '-' + String(progress.timeFrame || 'tf'))}>
                              <td>{progress.timeFrame}</td>
                              <td>{progress.conditionType}</td>
                              <td>{progress.signalKey}</td>
                              <td>{progress.matched ? 'YES' : 'NO'}</td>
                              <td>{progress.matchedDirection || '-'}</td>
                              <td>{progress.matchedCandleCloseTime || '-'}</td>
                              <td>{progress.lastCheckedCandleCloseTime || '-'}</td>
                              <td>{progress.status || '-'}</td>
                              <td>{progress.statusReason || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            )}
            </React.Fragment>
          ))}

          {/* Ã¢Å“â€¦ Add Row */}
          <tr>
            <td>+</td>
            <td></td>
            {Object.keys(newRow).map((key) => (
                <td key={key}>
                    {typeof newRow[key] === 'boolean' ? (
                    <input
                        type="checkbox"
                        checked={newRow[key]}
                        onChange={(e) => setNewRow({ ...newRow, [key]: e.target.checked })}
                    />
                    ) : key === 'symbol' ? (
                    <select
                        value={newRow.symbol}
                        onChange={(e) => setNewRow({ ...newRow, symbol: e.target.value })}
                    >
                        <option value="">Select</option>
                        {coinDropdownOptions.map((coin, i) => (
                        <option key={i} value={coin}>{coin}</option>
                        ))}
                    </select>
                    ) : key === 'orderType' ? (
                    <select
                        value={newRow.orderType}
                        onChange={(e) => setNewRow({ ...newRow, orderType: e.target.value })}
                    >
                        <option value="">Select</option>
                        {orderTypeOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'tradeType' ? (
                    <select
                        value={newRow.tradeType}
                        onChange={(e) => setNewRow({ ...newRow, tradeType: e.target.value })}
                    >
                        <option value="">Select</option>
                        {tradeTypeOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'timeFrame' ? (
                    <select
                        value={newRow.timeFrame}
                        onChange={(e) => setNewRow({ ...newRow, timeFrame: e.target.value })}
                    >
                        <option value="">Select</option>
                        {timeFrameOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'attemptAllowed' ? (
                    <select
                        value={newRow.attemptAllowed}
                        onChange={(e) => setNewRow({ ...newRow, attemptAllowed: e.target.value })}
                    >
                        <option value="">Select</option>
                        {attemptAllowedOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'attemptCompleted' ? (
                    <select
                        value={newRow.attemptCompleted}
                        onChange={(e) => setNewRow({ ...newRow, attemptCompleted: e.target.value })}
                    >
                        <option value="">Select</option>
                        {attemptCompletedOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'activeFlag' ? (
                    <select
                        value={newRow.activeFlag}
                        onChange={(e) => setNewRow({ ...newRow, activeFlag: e.target.value })}
                    >
                        <option value="">Select</option>
                        {activeFlagOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'tradeSide' ? (
                    <select
                        value={newRow.tradeSide}
                        onChange={(e) => setNewRow({ ...newRow, tradeSide: e.target.value })}
                    >
                        <option value="">Select</option>
                        {tradeSideOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'tradeEntryType' ? (
                    <select
                        value={newRow.tradeEntryType}
                        onChange={(e) => setNewRow({ ...newRow, tradeEntryType: e.target.value })}
                    >
                        <option value="">Select</option>
                        {tradeEntryTypeOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ) : key === 'tradeEntryTrackerType' ? (
                        <select
                        value={newRow.tradeEntryTrackerType}
                        onChange={(e) =>
                            setNewRow({ ...newRow, tradeEntryTrackerType: e.target.value })
                        }
                        >
                        <option value="">Select</option>
                        {getTradeEntryTrackerTypeOptions().map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                        ))}
                        </select>
                    ) : key === 'leverage' ? (
                    <select
                        value={newRow.leverage}
                        onChange={(e) => setNewRow({ ...newRow, leverage: e.target.value })}
                    >
                        <option value="">Select</option>
                        {leverageOptions.map((opt, i) => (
                        <option key={i} value={opt}>{opt}</option>
                        ))}
                    </select>
                    ): (
                    <input
                        value={newRow[key]}
                        onChange={(e) => setNewRow({ ...newRow, [key]: e.target.value })}
                    />
                    )}
                </td>
                ))}

                <td className="actions-cell">
                <button className="btn btn--add" onClick={handleAdd}>Add</button>
                </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div className="trade-entry-record-count">
      Showing {filteredList.length} of {dataList.length} records
    </div>
  </div>
);

}

export default ManageTradeEntryTracker;
